import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface SnackbarItem {
    id: string
    message: string
    type: 'success' | 'error' | 'warning' | 'info'
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center' | 'center'
    duration?: number
    showCloseButton?: boolean
}

interface UIState {
    theme: 'light' | 'dark' | 'system'
    sidebarOpen: boolean
    mobileMenuOpen: boolean
    currentView: 'grid' | 'list'
    loading: {
        global: boolean
        gigs: boolean
        profile: boolean
        applications: boolean
        messages: boolean
    }
    modal: {
        isOpen: boolean
        type: string | null
        data: any
    }
    notifications: {
        isOpen: boolean
        unreadCount: number
    }
    filters: {
        isOpen: boolean
        activeFilters: Record<string, any>
    }
    searchQuery: string
    location: {
        city: string
        coordinates: [number, number] | null
        radius: number
    }
    snackbars: SnackbarItem[]
}

const initialState: UIState = {
    theme: (localStorage.getItem('theme') as 'light' | 'dark' | 'system') || 'system',
    sidebarOpen: false,
    mobileMenuOpen: false,
    currentView: (localStorage.getItem('viewMode') as 'grid' | 'list') || 'grid',
    loading: {
        global: false,
        gigs: false,
        profile: false,
        applications: false,
        messages: false,
    },
    modal: {
        isOpen: false,
        type: null,
        data: null,
    },
    notifications: {
        isOpen: false,
        unreadCount: 0,
    },
    filters: {
        isOpen: false,
        activeFilters: {},
    },
    searchQuery: '',
    location: {
        city: '',
        coordinates: null,
        radius: 10, // km
    },
    snackbars: [],
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
            state.theme = action.payload
            localStorage.setItem('theme', action.payload)
        },
        toggleSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload
        },
        toggleMobileMenu: (state) => {
            state.mobileMenuOpen = !state.mobileMenuOpen
        },
        setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.mobileMenuOpen = action.payload
        },
        setCurrentView: (state, action: PayloadAction<'grid' | 'list'>) => {
            state.currentView = action.payload
            localStorage.setItem('viewMode', action.payload)
        },
        setLoading: (state, action: PayloadAction<{ key: keyof UIState['loading']; value: boolean }>) => {
            state.loading[action.payload.key] = action.payload.value
        },
        setGlobalLoading: (state, action: PayloadAction<boolean>) => {
            state.loading.global = action.payload
        },
        openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
            state.modal.isOpen = true
            state.modal.type = action.payload.type
            state.modal.data = action.payload.data || null
        },
        closeModal: (state) => {
            state.modal.isOpen = false
            state.modal.type = null
            state.modal.data = null
        },
        toggleNotifications: (state) => {
            state.notifications.isOpen = !state.notifications.isOpen
        },
        setNotificationsOpen: (state, action: PayloadAction<boolean>) => {
            state.notifications.isOpen = action.payload
        },
        setUnreadNotifications: (state, action: PayloadAction<number>) => {
            state.notifications.unreadCount = action.payload
        },
        incrementUnreadNotifications: (state) => {
            state.notifications.unreadCount += 1
        },
        decrementUnreadNotifications: (state) => {
            if (state.notifications.unreadCount > 0) {
                state.notifications.unreadCount -= 1
            }
        },
        toggleFilters: (state) => {
            state.filters.isOpen = !state.filters.isOpen
        },
        setFiltersOpen: (state, action: PayloadAction<boolean>) => {
            state.filters.isOpen = action.payload
        },
        setActiveFilters: (state, action: PayloadAction<Record<string, any>>) => {
            state.filters.activeFilters = action.payload
        },
        updateFilter: (state, action: PayloadAction<{ key: string; value: any }>) => {
            state.filters.activeFilters[action.payload.key] = action.payload.value
        },
        removeFilter: (state, action: PayloadAction<string>) => {
            delete state.filters.activeFilters[action.payload]
        },
        clearFilters: (state) => {
            state.filters.activeFilters = {}
        },
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload
        },
        setLocation: (state, action: PayloadAction<{ city: string; coordinates?: [number, number] }>) => {
            state.location.city = action.payload.city
            if (action.payload.coordinates) {
                state.location.coordinates = action.payload.coordinates
            }
        },
        setLocationRadius: (state, action: PayloadAction<number>) => {
            state.location.radius = action.payload
        },
        resetUI: (state) => {
            Object.assign(state, initialState)
        },
        showSnackbar: (state, action: PayloadAction<Omit<SnackbarItem, 'id'>>) => {
            const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
            const snackbar: SnackbarItem = {
                id,
                ...action.payload,
                position: action.payload.position || 'bottom-right',
                duration: action.payload.duration ?? 5000,
                showCloseButton: action.payload.showCloseButton ?? true
            }
            state.snackbars.push(snackbar)
        },
        hideSnackbar: (state, action: PayloadAction<string>) => {
            state.snackbars = state.snackbars.filter(snackbar => snackbar.id !== action.payload)
        },
        clearSnackbars: (state) => {
            state.snackbars = []
        },
    },
})

export const {
    setTheme,
    toggleSidebar,
    setSidebarOpen,
    toggleMobileMenu,
    setMobileMenuOpen,
    setCurrentView,
    setLoading,
    setGlobalLoading,
    openModal,
    closeModal,
    toggleNotifications,
    setNotificationsOpen,
    setUnreadNotifications,
    incrementUnreadNotifications,
    decrementUnreadNotifications,
    toggleFilters,
    setFiltersOpen,
    setActiveFilters,
    updateFilter,
    removeFilter,
    clearFilters,
    setSearchQuery,
    setLocation,
    setLocationRadius,
    resetUI,
    showSnackbar,
    hideSnackbar,
    clearSnackbars,
} = uiSlice.actions

export default uiSlice.reducer